import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import useMenuLogic from '../../../hooks/useMenuLogic';
import MenuItem from './MenuItem';
import LogoutMenuOption from './LogoutMenuOption';
import CloseIcon from '../CloseIcon';
import Logo from '../Logo';
import LogoBlue from '../../../assets/images/Logo-Blue.png';

/**
 * MobileMenu component
 *
 * Renders a responsive navigation menu for mobile devices, including:
 * - A header bar with a brand logo and a close button
 * - Menu items filtered by the user's role
 * - A logout option
 *
 * This menu is rendered via a React Portal and supports closing on outside clicks.
 *
 * @param {boolean} isOpen - Determines whether the menu is visible.
 * @param {Function} onClose - Callback function to close the menu.
 * @returns {JSX.Element|null} The mobile menu UI or null if not open.
 */
const MobileMenu = ({ isOpen, onClose }) => {
  const menuRef = useRef(null);
  const user = useSelector((state) => state?.auth?.user);
  const userRole = user?.role;
  const { isActive, handleNavigation, filteredMenuItems } = useMenuLogic(userRole, onClose);

  if (!user || !isOpen) return null;

  return ReactDOM.createPortal(
    <>
      {/* Dark background */}
      <div className="fixed inset-0 z-30 bg-black/20" onClick={onClose} role="presentation" />

      {/* Menu Container */}
      <div
        ref={menuRef}
        className="fixed top-0 left-0 right-0 h-auto z-40 bg-white text-black shadow-lg overflow-y-auto rounded-b-xl"
      >
        {/* Header area: Logo + Close button */}
        <div className="flex items-center justify-between p-4 sticky top-0 bg-white pr-6">
          <div className="flex-1">
            <Logo URL={LogoBlue} name="Logo Blue" variant="small" />
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors mt-[10px] sm:mt-[30px] md:mr-[20px]"
            aria-label="Close menu"
          >
            <CloseIcon size="24" />
          </button>
        </div>

        {/* Menu Items */}
        <div className="px-4 pb-6">
          {filteredMenuItems.map((item) => (
            <MenuItem
              key={item.path}
              copy={item.copy}
              IconComponent={item.Icon}
              active={isActive(item.path)}
              onClick={() => handleNavigation(item.path)}
            />
          ))}

          {/* Logout Option */}
          <div className="mt-10">
            <LogoutMenuOption onClose={onClose} />
          </div>
        </div>
      </div>
    </>,
    document.getElementById('mobile-menu-portal-root'),
  );
};

export default MobileMenu;
